import React, { useEffect } from "react";
import { MapPin, X } from "lucide-react";
import { useSelectedLocation } from "../hooks/useSelectedLocation";

const LocationModal = ({
  showLocationModal,
  setShowLocationModal,
  allStates,
  allLgas,
  selectedState,
  selectedLga,
  handleStateSelect,
  handleLgaSelect,
  selectedLocation,
  handleContinue,
  loadingStates,
  loadingLgas,
}) => {
  const { getLocation } = useSelectedLocation();

  useEffect(() => {
    if (showLocationModal && !selectedState) {
      const savedLocation = getLocation();
      if (savedLocation) {
        const { state, lga } = savedLocation;
        if (state && allStates.length > 0) {
          const foundState = allStates.find((s) => s.name === state);
          if (foundState) {
            handleStateSelect(foundState);
            if (lga && allLgas.length > 0) {
              const foundLga = allLgas.find((l) => l.name === lga);
              if (foundLga) handleLgaSelect(foundLga);
            }
          }
        }
      }
    }
  }, [
    showLocationModal,
    selectedState,
    allStates,
    allLgas,
    handleStateSelect,
    handleLgaSelect,
    getLocation,
  ]);

  if (!showLocationModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 text-center border-b relative">
          <button
            onClick={() => setShowLocationModal(false)}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition"
            title="Close"
          >
            <X size={24} className="text-gray-600" />
          </button>
          <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin size={32} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Choose Your Location
          </h2>
          <p className="text-gray-600 text-md">
            Select your preferred location to get the best local deals
          </p>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="space-y-4">
            {/* State Selection */}
            <div>
              <label className="block font-medium text-gray-700 mb-2">
                Select State
              </label>
              <select
                value={selectedState?.name || ""}
                onChange={(e) => {
                  const state = allStates.find(
                    (s) => s.name === e.target.value,
                  );
                  if (state) handleStateSelect(state);
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                disabled={loadingStates}
              >
                <option value="">
                  {loadingStates ? "Loading states..." : "Choose a state"}
                </option>
                {allStates.map((state) => (
                  <option key={state.id} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            {/* LGA Selection */}
            <div>
              <label className="block  font-medium text-gray-700 mb-2">
                Select Location
              </label>
              <select
                value={selectedLga?.name || ""}
                onChange={(e) => {
                  const lga = allLgas.find((l) => l.name === e.target.value);
                  if (lga) handleLgaSelect(lga);
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                disabled={loadingLgas || !selectedState}
              >
                <option value="">
                  {loadingLgas
                    ? "Loading Locations..."
                    : selectedState
                      ? "Choose a Location"
                      : "Select a state first"}
                </option>
                {allLgas.map((lga) => (
                  <option key={lga.id} value={lga.name}>
                    {lga.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedLocation && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-yellow-600" />
                  <span className=" font-medium text-yellow-800">
                    Selected: {selectedLocation.name}, {selectedLocation.state}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t">
          <button
            onClick={handleContinue}
            disabled={!selectedLocation}
            className={`w-full py-4 rounded-lg text-xl font-semibold transition ${
              selectedLocation
                ? "bg-black text-white hover:bg-gray-800"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationModal;
